import React, { useState } from 'react'

export default function ContactForm({ dark = false }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field
  })
  const [status, setStatus] = useState({ type: '', message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setStatus({ type: '', message: '' })

    // Basic validation
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.description.trim()) {
      setStatus({ type: 'error', message: 'Udfyld venligst alle påkrævede felter.' })
      setIsSubmitting(false)
      return
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi vender tilbage til dig hurtigst muligt.',
        })
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        setStatus({
          type: 'error',
          message: errData.message || 'Der skete en fejl under indsendelsen. Prøv venligst igen.',
        })
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setStatus({
        type: 'error',
        message: 'Der kunne ikke oprettes forbindelse til serveren. Prøv igen senere.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`w-full p-8 md:p-12 shadow-2xl flex flex-col gap-6 rounded-none ${
      dark 
        ? 'bg-[#1c1c1c] text-white border border-zinc-800/50' 
        : 'bg-zinc-50 text-zinc-800 border border-zinc-200'
    }`}>
      
      {/* Header Text Group */}
      <div className="space-y-2">
        <h2 className={`text-2xl md:text-3xl font-extrabold tracking-tight uppercase ${
          dark ? 'text-white' : 'text-neutral-950'
        }`}>
          Få et uforpligtende tilbud
        </h2>
        <p className={`text-sm ${
          dark ? 'text-zinc-300' : 'text-zinc-500'
        }`}>
          Send dine kontaktoplysninger her, så ringer vi dig op.
        </p>
      </div>

      {status.message && (
        <div
          className={`p-4 text-xs font-bold uppercase tracking-wider rounded-none ${
            status.type === 'success'
              ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
              : 'bg-red-500/10 text-red-500 border border-red-500/20'
          }`}
        >
          {status.message}
        </div>
      )}

      {/* Form Fields */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Honeypot field - absolutely hidden from humans */}
        <div className="sr-only absolute" aria-hidden="true">
          <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke)</label>
          <input
            type="text"
            id="website_url"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        <div className="relative">
          <input 
            type="text" 
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Navn *" 
            className="w-full bg-white text-zinc-900 px-4 py-3.5 outline-none focus:ring-2 focus:ring-red-700 transition-all duration-150 rounded-none placeholder-zinc-500 font-medium text-sm border border-zinc-200"
          />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <input 
              type="email" 
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="E-mail *" 
              className="w-full bg-white text-zinc-900 px-4 py-3.5 outline-none focus:ring-2 focus:ring-red-700 transition-all duration-150 rounded-none placeholder-zinc-500 font-medium text-sm border border-zinc-200"
            />
          </div>
          
          <div>
            <input 
              type="tel" 
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={handleChange}
              placeholder="Telefonnummer *" 
              className="w-full bg-white text-zinc-900 px-4 py-3.5 outline-none focus:ring-2 focus:ring-red-700 transition-all duration-150 rounded-none placeholder-zinc-500 font-medium text-sm border border-zinc-200"
            />
          </div>
        </div>

        <div>
          <textarea
            id="description"
            name="description"
            required
            rows="4"
            value={formData.description}
            onChange={handleChange}
            placeholder="Beskriv dit projekt kort... *"
            className="w-full bg-white text-zinc-900 px-4 py-3.5 outline-none focus:ring-2 focus:ring-red-700 transition-all duration-150 rounded-none placeholder-zinc-500 font-medium text-sm border border-zinc-200 resize-none"
          />
        </div>

        {/* Submit Button */}
        <button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full bg-red-700 hover:bg-red-800 text-white py-4 px-6 font-bold uppercase tracking-wider transition-colors duration-200 rounded-none cursor-pointer flex items-center justify-center gap-2 text-sm"
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-spinner animate-spin" />
              Sender...
            </>
          ) : (
            <>
              Ring mig op
              <i className="fa-solid fa-arrow-right text-xs" />
            </>
          )}
        </button>
      </form>
    </div>
  )
}
